import { useState, useEffect } from 'react';
import { transformApiAnnouncementToDisplay } from '../../../../modules/Feeds/mappers/feedUtility';

/**
 * Removes all HTML tags from a string
 * @param {string} htmlString - String containing HTML tags
 * @returns {string} Plain text without HTML tags
 */
const stripHtmlTags = (htmlString) => {
  if (!htmlString || typeof htmlString !== 'string') {
    return '';
  }
  // Remove HTML tags using regex
  return htmlString.replace(/<[^>]*>/g, '').trim();
};

/**
 * Custom hook for transforming announcement data provided from parent
 * 
 * @param {Object} announcementData - Announcement data object to transform and display
 * @returns {Object} Hook return object containing:
 *   @returns {Object|null} announcement - The transformed announcement data, or null if transformation failed
 *   @returns {boolean} isLoading - Always false since no API call is made
 *   @returns {Error|null} error - Any error that occurred during transformation, or null if no error
 */
export const useFeedAnnouncementHook = (announcementData) => {
  const [announcement, setAnnouncement] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset state when announcementData changes
    setAnnouncement(null);
    setError(null);

    // If no announcementData is provided, return early
    if (!announcementData) {
      return;
    }

    try {
      // Ensure required fields exist with defaults for audit API data
      const normalizedData = {
        id: announcementData.id,
        subject: stripHtmlTags(announcementData.subject || ''),
        body: announcementData.body || announcementData.description || '',
        senderName: announcementData.senderName || 'Unknown Admin',
        createdBy: announcementData.createdBy || announcementData.senderId || '',
        createdAt: announcementData.createdAt || new Date().toISOString(),
        membersInfo: announcementData.membersInfo || [],
        metadata: announcementData.metadata || {},
        readAt: announcementData.readAt || null,
        isDeleted: announcementData.isDeleted || false,
      };

      // Transform the normalized data to display format
      const transformedAnnouncement = transformApiAnnouncementToDisplay(normalizedData);
      // Remove tags from the transformed announcement
      transformedAnnouncement.tags = [];
      // Preserve isDeleted property for menu options
      transformedAnnouncement.isDeleted = normalizedData.isDeleted;
      setAnnouncement(transformedAnnouncement);
    } catch (err) {
      console.error('[useFeedAnnouncementHook] Failed to transform announcement data:', err);
      setError(err);
      setAnnouncement(null);
    }
  }, [announcementData]);

  return { announcement, isLoading: false, error };
};

